import { combineForms } from "react-redux-form";

import UserReducer from "../../shared/state/authReducer";
import MessagingReducer from "../../shared/state/messagingReducer";
import { chain, clear, fromApi, keyed, mergePage, upsertSingle, when, scope } from "../../shared/state/reducers";
import { formModels as SharedFormModels } from "./../../shared/state/forms";
import { Actions } from "./actions";
import { SharedActions } from "../../shared/flux/actions/sharedActions"
import CurrencyReducer from "./currencyReducer";
import { formModels as WebFormModels } from "./forms";

const formModels = { ...WebFormModels, ...SharedFormModels };

const webReducers: IStoreReducers<IWebStoreReducerState> = {
  forms: combineForms(formModels, "forms"),

  user: UserReducer,

  currency: CurrencyReducer,
  currencies: fromApi<ICurrency[], ICurrency[]>(Actions.LoadAllCurrencies, x => x != null ? x.filter(c => c.availableToCustomers) : []),
  cardTypes: fromApi<ICardType[], ICardType[]>(Actions.GetAllCardTypes, x => x),

  enquiryItineraryCurrencies: chain(
    when(Actions.ClearCurrentItinerary, clear()),
    fromApi<IEnquiryItineraryCurrencies, IEnquiryItineraryCurrencies>(Actions.GetCurrenciesForQuote, x => x),
  ),

  messagesByEnquiryId: MessagingReducer,

  lto: fromApi<ILto, ILto>([Actions.GetLtoById, Actions.GetLtoBySlug], x => x),
  ltos: fromApi<ILtoOutput, ILto[]>(Actions.GetLtosByLocationSlug, x => x.ltos),
  ltoPrimaryLocation: fromApi<ILocation, ILocation>(Actions.GetLtoPrimaryLocationBySlug, x => x),
  ltoTerms: fromApi<ILtoTerms, ILtoTerms>(Actions.GetLtoTermsById, x => x),

  region: fromApi<IRegion, IRegion>(Actions.GetRegionBySlug, x => x),
  regions: fromApi<IPage<IRegionSummary>, IRegionSummary[]>(Actions.GetAllRegions, x => x.payload),

  country: fromApi<ICountry, ICountry>(Actions.GetCountryBySlug, x => x),
  countries: fromApi<IPage<ICountrySummary>, ICountrySummary[]>(Actions.GetAllCountries, x => x.payload),
  regionCountries: fromApi<ICountriesOutput, ICountrySummary[]>(Actions.GetCountriesByRegionSlug, x => x.countries),
  featuredLocations: fromApi<IPage<ILocationSummary>, ILocationSummary[]>(Actions.GetFeaturedLocations, x => x.payload),

  destination: fromApi<IDestination, IDestination>(Actions.GetDestinationBySlug, x => x),
  destinations: fromApi<IPage<IDestinationSummary>, IDestinationSummary[]>(Actions.GetDestinationsByCountrySlug, x => x.payload),

  currentLocationImage: fromApi<IAccomodationLocationImage, IAccomodationLocationImage>(Actions.GetImageByFileName, x => x),

  sampleItinerary: fromApi<ISampleItinerarySummary, ISampleItinerarySummary>(Actions.GetSampleItineraryBySlug, x => x),
  excursions: fromApi<IPage<IExcursion>, IPage<IExcursion>>(Actions.GetExcursionsByLtoIdAndCountryId, x => x),

  customerEnquiries: fromApi<IEnquirySummariesOutput, IEnquirySummaryOutput[]>(Actions.GetEnquiriesForCustomer, x => x.enquiries),
  customerEnquiry: chain(
    when(Actions.ClearCurrentEnquiry, clear()),
    fromApi<IApiResponse<IEnquiryOutput>, IEnquiryOutput>(Actions.GetEnquiryById, x => x.data),
    fromApi<IEnquiryOutput, IEnquiryOutput>([
      Actions.AddNewTravelerDetails,
      Actions.UpdateTravelerDetails,
      Actions.AddNewLeadTravelerDetails,
      Actions.UpdateLeadTravelerDetails,
    ], x => x),
  ),

  customerItineraries: chain(
    when(Actions.ClearCurrentEnquiry, clear()),
    keyed(
      Actions.GetEnquiryItinerary,
      fromApi<IEnquiryItinerarySummary, IEnquiryItinerarySummary>(Actions.GetEnquiryItinerary, x => x),
    ),
  ),

  customerEnquiryPayments: chain(
    when(Actions.ClearCurrentEnquiry, clear()),
    fromApi<IPaymentsOutput, IPaymentsOutput>(Actions.GetEnquiryPayments, x => x),
  ),

  customerEnquiryTerms: chain(
    when(Actions.ClearCurrentEnquiry, clear()),
    fromApi<IApiResponse<IBookingTerms>, IBookingTerms>(SharedActions.Enquiries.GetEnquiryTerms, x => x.data),
  ),

  contentByCollection: keyed(
    Actions.GetContentPage,
    mergePage<IContent, IContent>(Actions.GetContentPage, x => x, x => x.id),
  ),

  contentThemes: chain(
    upsertSingle<IFetchApiResponse<IApiResponse<ITheme>>, ITheme>(Actions.GetContentTheme, x => x.json.data, x => x.id),
    mergePage<ITheme, ITheme>(Actions.GetInspirationThemes, x => x, x => x.id),
  ),
  contentItem: fromApi<IApiResponse<IContent>, IContent>(Actions.GetContentSingle, (x) => x.data),

  enquiryThemes: chain(
    upsertSingle<IFetchApiResponse<IApiResponse<ITheme>>, ITheme>(Actions.GetEnquiryTheme, x => x.json.data, x => x.id),
    mergePage<ITheme, ITheme>(Actions.GetEnquiryThemes, x => x, x => x.id),
  ),

  ageRanges: fromApi<IApiResponse<IAgeRange[]>, IAgeRange[]>(Actions.GetAllAgeRanges, x => x.data),

  settings: keyed(Actions.GetSettings, fromApi(Actions.GetSettings, x => x)) as any,
};

export default webReducers;
